import { ArrowRight, IdCard, type LucideIcon, RotateCcw, Sun, Wallpaper } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaReveal } from "@/shared/components/motion/ColocaReveal";
import type { VerificationTranslations } from "@/shared/lang/interfaces/verification";
import useTranslations from "@/shared/lang/useTranslations";

import { CapturePhotoExamples } from "./CapturePhotoExamples";

type VerificationCopyKey = keyof VerificationTranslations;

interface GuideTip {
  icon: LucideIcon;
  title: VerificationCopyKey;
  body: VerificationCopyKey;
}

/** What decides whether a photo passes: the light, the place, and having the physical document. */
const GUIDE_TIPS: readonly GuideTip[] = [
  { icon: Sun, title: "photo_guide_tip_light_title", body: "photo_guide_tip_light_body" },
  { icon: Wallpaper, title: "photo_guide_tip_place_title", body: "photo_guide_tip_place_body" },
  { icon: IdCard, title: "photo_guide_tip_document_title", body: "photo_guide_tip_document_body" },
];

export interface CapturePhotoGuideProps {
  /** The person read the guide: the capture starts. */
  onReady: () => void;
}

/**
 * The photo guide, right before the document and selfie capture, kept to what a person can check by looking:
 * three things about the place (light, background, the physical document) and the guide's "así sí / así no"
 * pictures for the document and the selfie. A photo that misses them goes to a manual review that can take
 * weeks, so it is said once, up front. No settings to change and no acknowledgement: one button starts.
 */
export function CapturePhotoGuide({ onReady }: CapturePhotoGuideProps) {
  const { t } = useTranslations("verification");

  return (
    <ColocaReveal className="space-y-6" stagger={0.05}>
      <div data-reveal className="space-y-2 text-center">
        <p className="text-xs font-semibold uppercase tracking-wider text-primary">
          {t("photo_guide_eyebrow")}
        </p>
        <h1 className="text-2xl font-semibold text-foreground">{t("photo_guide_title")}</h1>
        <p className="text-sm text-muted-foreground">{t("photo_guide_lead")}</p>
      </div>

      <ul data-reveal className="grid gap-3 rounded-xl bg-muted/50 p-3 sm:grid-cols-3 sm:gap-2">
        {GUIDE_TIPS.map(({ icon: Icon, title, body }) => (
          <li key={title} className="flex items-start gap-2.5 sm:flex-col sm:gap-1.5">
            <span className="inline-flex size-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Icon className="size-3.5" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">{t(title)}</p>
              <p className="text-xs text-muted-foreground">{t(body)}</p>
            </div>
          </li>
        ))}
      </ul>

      <section aria-labelledby="photo-guide-document" className="space-y-3">
        <div data-reveal>
          <h2 id="photo-guide-document" className="text-sm font-semibold text-foreground">
            {t("photo_guide_document_title")}
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">{t("photo_guide_document_caption")}</p>
        </div>
        <CapturePhotoExamples kind="document" />
      </section>

      <section aria-labelledby="photo-guide-selfie" className="space-y-3">
        <div data-reveal>
          <h2 id="photo-guide-selfie" className="text-sm font-semibold text-foreground">
            {t("photo_guide_selfie_title")}
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">{t("photo_guide_selfie_caption")}</p>
        </div>
        <CapturePhotoExamples kind="selfie" />
      </section>

      <div data-reveal className="space-y-3 border-t border-border pt-5">
        <ColocaButton variant="primary" className="w-full" iconTrailing={ArrowRight} onClick={onReady}>
          {t("photo_guide_cta")}
        </ColocaButton>
        <p className="flex items-start justify-center gap-1.5 text-center text-xs text-muted-foreground">
          <RotateCcw className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
          <span>{t("photo_guide_rejected_note")}</span>
        </p>
      </div>
    </ColocaReveal>
  );
}
